import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import PianoSelector from "../components/PianoSelector";
import {
  useInstrument,
  instrumentLabel,
  instrumentEmoji,
  instrumentPalette,
} from "../context/InstrumentContext";
import { Plus, ChevronRight, Trophy, Music2, Trash2, X } from "lucide-react";

const ALL_KEYS = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];

const COLOR_VARIANTS = {
  flute:     [["silver","#C0C0C0"],["gold","#D4AF37"],["rose_gold","#B76E79"],["black_nickel","#2b2b2b"]],
  trumpet:   [["gold_lacquer","#C9A24A"],["silver","#C0C0C0"],["rose_gold","#B76E79"],["black","#222"]],
  saxophone: [["gold_lacquer","#C9A24A"],["silver","#C0C0C0"],["black_nickel","#2b2b2b"],["cognac","#A0522D"]],
  clarinet:  [["ebony","#1a1a1a"],["rosewood","#65352E"],["cocobolo","#7A3220"],["hard_rubber","#2b2b2b"]],
};

function ColorSwatchPicker({ instrumentKey, value, onChange }) {
  const swatches = COLOR_VARIANTS[instrumentKey] || [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={`color-picker-${instrumentKey}`}>
      {swatches.map(([id, hex]) => {
        const active = value === id;
        return (
          <button key={id} type="button" onClick={() => onChange(id)}
            data-testid={`color-swatch-${id}`} aria-pressed={active}
            className={`rounded-xl border-2 transition-all px-3 py-2 flex items-center gap-2 ${active ? "ring-4" : "hover:-translate-y-0.5"}`}
            style={{ borderColor: active ? "var(--app-accent)" : "var(--app-border)" }}>
            <span className="w-6 h-6 rounded-full border" style={{ background: hex, borderColor: "#0002" }} />
            <span className="text-sm font-semibold capitalize">{id.replace(/_/g, " ")}</span>
          </button>
        );
      })}
    </div>
  );
}

function InstrumentEditor({ instrumentKey, initial, onSave, dream = false, testPrefix = "inst" }) {
  const t = useT();
  const { customInstruments } = useInstrument();
  const [data, setData] = useState(initial || {});
  const fileRef = useRef();
  useEffect(() => { setData(initial || {}); }, [initial, instrumentKey]);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setData((d) => ({ ...d, photo_data_url: reader.result }));
    reader.readAsDataURL(f);
  };
  const save = async () => { await onSave(data); toast.success(t("Saved")); };
  const isPiano = instrumentKey === "piano";
  const isCustom = instrumentKey?.startsWith("custom_");
  const hasColorVariants = !isCustom && Object.prototype.hasOwnProperty.call(COLOR_VARIANTS, instrumentKey);
  const customLabel = customInstruments?.[instrumentKey]?.label || instrumentKey;
  return (
    <div className="space-y-5" data-testid={`${testPrefix}-editor`}>
      {isPiano ? (
        <div>
          <Label>{t("Type")}</Label>
          <PianoSelector value={data.type} onChange={(v) => setData({ ...data, type: v })} />
        </div>
      ) : hasColorVariants ? (
        <div>
          <Label>{t("Finish / color")}</Label>
          <ColorSwatchPicker instrumentKey={instrumentKey} value={data.type}
                             onChange={(v) => setData({ ...data, type: v })} />
        </div>
      ) : null}
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>{t("Make / brand")}</Label><Input value={data.make || ""} onChange={(e) => setData({ ...data, make: e.target.value })} data-testid={`${testPrefix}-make`} /></div>
        <div><Label>{t("Model")}</Label><Input value={data.model || ""} onChange={(e) => setData({ ...data, model: e.target.value })} data-testid={`${testPrefix}-model`} /></div>
      </div>
      {!dream && (
        <div className="flex items-center gap-3 flex-wrap">
          <Switch checked={!!data.has_nickname} onCheckedChange={(v) => setData({ ...data, has_nickname: v })} data-testid={`${testPrefix}-nick-toggle`} />
          <span>My {(isCustom ? customLabel : instrumentLabel(instrumentKey)).toLowerCase()} has a nickname</span>
          {data.has_nickname && (
            <Input className="max-w-xs" value={data.nickname || ""} onChange={(e) => setData({ ...data, nickname: e.target.value })}
                   placeholder={t("Nickname")} data-testid={`${testPrefix}-nickname`} />
          )}
        </div>
      )}
      <div>
        <Label>{dream ? t("Dream photo") : t("Photo")}</Label>
        <div className="flex items-center gap-4">
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid={`${testPrefix}-photo-input`} />
          {data.photo_data_url && <img alt="instrument" src={data.photo_data_url} className="w-28 h-20 object-cover rounded-xl" />}
        </div>
      </div>
      <Button onClick={save} className="btn-pill btn-primary h-11" data-testid={`${testPrefix}-save`}>{t("Save")}</Button>
    </div>
  );
}

/** Small 12-dot scale-progress indicator, like a fighter's special-moves bar. */
function ScaleDotRow({ scalesByKey = {}, fg = "#FFFFFF" }) {
  return (
    <div className="flex items-center gap-1" data-testid="scale-dots">
      {ALL_KEYS.map((k) => {
        const s = scalesByKey[k] || "not_started";
        const color = {
          not_started:   "rgba(255,255,255,0.15)",
          just_started:  "#FFD166",
          still_working: "#FFB703",
          almost_there:  "#F08C00",
          good_enough:   "#3AB795",
          perfection:    "#E63946",
        }[s] || "rgba(255,255,255,0.15)";
        return (
          <span
            key={k}
            title={`${k}: ${s.replace(/_/g, " ")}`}
            className="rounded-full"
            style={{
              width: 10, height: 10,
              background: color,
              boxShadow: s === "perfection" ? `0 0 6px ${color}` : "none",
              border: `1px solid ${fg}33`,
            }}
          />
        );
      })}
    </div>
  );
}

/** Big fighter-select card. Click it → open detail dialog. */
function FighterCard({ entry, onOpen }) {
  const key = entry.instrument;
  const customMeta = entry.custom_meta;
  const [primary, secondary, fg] = instrumentPalette(key);
  const label = customMeta?.label || instrumentLabel(key);
  const emoji = customMeta?.emoji || instrumentEmoji(key);
  const portraitPhoto = entry.profile?.photo_data_url || customMeta?.photo_data_url;
  const nickname = entry.profile?.has_nickname && entry.profile?.nickname;
  const trophies = entry.trophies || 0;
  const scalesMastered = entry.scales_perfected || 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`fighter-card-${key}`}
      className="group relative text-left rounded-2xl overflow-hidden border-4 transition-all hover:-translate-y-0.5 hover:shadow-2xl focus:outline-none focus:ring-4"
      style={{
        borderColor: primary,
        background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
        color: fg,
        boxShadow: "0 4px 0 rgba(0,0,0,0.35), inset 0 2px 0 rgba(255,255,255,0.12)",
      }}
    >
      {/* Scan-line texture */}
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0 1px, transparent 1px 4px)" }} />
      <div className="relative flex items-center gap-4 p-4 md:p-5">
        {/* Portrait */}
        {portraitPhoto ? (
          <img alt="" src={portraitPhoto}
               className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover shrink-0 border-2"
               style={{ borderColor: fg }} />
        ) : (
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl shrink-0 flex items-center justify-center text-5xl md:text-6xl"
               style={{ background: "rgba(255,255,255,0.12)", border: `2px solid ${fg}66` }}
               aria-hidden>{emoji}</div>
        )}

        {/* Name + stats */}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-2xl md:text-3xl tracking-tight uppercase leading-none drop-shadow-sm">
            {label}
          </div>
          {nickname && (
            <div className="italic text-sm md:text-base opacity-90 mt-0.5 truncate">
              "{entry.profile.nickname}"
            </div>
          )}
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs md:text-sm font-bold uppercase tracking-wide px-2 py-1 rounded-md"
                  style={{ background: "rgba(0,0,0,0.35)" }}>
              <Trophy className="w-3.5 h-3.5" /> {trophies}
            </span>
            <span className="inline-flex items-center gap-1 text-xs md:text-sm font-bold uppercase tracking-wide px-2 py-1 rounded-md"
                  style={{ background: "rgba(0,0,0,0.35)" }}>
              <Music2 className="w-3.5 h-3.5" /> {scalesMastered}/12
            </span>
          </div>
          <div className="mt-2">
            <ScaleDotRow scalesByKey={entry.scales_by_key} fg={fg} />
          </div>
        </div>

        <ChevronRight className="w-6 h-6 shrink-0 opacity-80 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </button>
  );
}

/** Full catalog — for the Add Instrument dialog. */
const CATALOG = [
  "piano","guitar","violin","cello","upright_bass",
  "drums","flute","saxophone","trumpet","clarinet",
  "voice","ukulele",
];

function AddInstrumentDialog({ open, onClose, currentKeys, onPick, onCreateCustom, isPremium, upsellLabel }) {
  const t = useT();
  const available = CATALOG.filter((k) => !currentKeys.includes(k));
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("Add an instrument")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm -mt-2" style={{ color: "var(--app-text-muted)" }}>
          {t("Pick one to add to your roster. You'll be able to fill in the details next.")}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
          {available.length === 0 && (
            <div className="col-span-full text-sm text-center py-6" style={{ color: "var(--app-text-muted)" }}>
              {t("You've already added every instrument we support. 🎉")}
            </div>
          )}
          {available.map((k) => {
            const [primary, secondary, fg] = instrumentPalette(k);
            return (
              <button
                key={k} type="button"
                onClick={() => onPick(k)}
                data-testid={`add-pick-${k}`}
                className="rounded-2xl p-4 border-2 transition-all hover:-translate-y-0.5 text-left"
                style={{
                  borderColor: primary,
                  background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
                  color: fg,
                }}
              >
                <div className="text-4xl">{instrumentEmoji(k)}</div>
                <div className="font-display font-bold text-lg mt-1">{instrumentLabel(k)}</div>
              </button>
            );
          })}
          {/* Studio Pro perk: create your own */}
          <button
            type="button"
            onClick={onCreateCustom}
            data-testid="add-pick-custom"
            className="rounded-2xl p-4 border-2 border-dashed transition-all hover:-translate-y-0.5 text-left flex flex-col"
            style={{
              borderColor: "var(--app-accent)",
              background: isPremium ? "rgba(255,183,3,0.10)" : "var(--app-surface-2)",
              color: "var(--app-text)",
            }}
          >
            <div className="text-4xl">➕</div>
            <div className="font-display font-bold text-lg mt-1">{t("Add your own")}</div>
            <div className="text-xs mt-1" style={{ color: "var(--app-text-muted)" }}>
              {isPremium ? t("Custom title + photo") : (upsellLabel || t("Studio Pro · $15.99/mo"))}
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomInstrumentDialog({ open, onClose, onCreate }) {
  const t = useT();
  const [label, setLabel] = useState("");
  const [emoji, setEmoji] = useState("🎼");
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!open) { setLabel(""); setEmoji("🎼"); setPhotoDataUrl(""); }
  }, [open]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error(t("Please pick an image under 3 MB.")); return; }
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) { toast.error(t("Please give your instrument a name.")); return; }
    setBusy(true);
    try {
      await onCreate({ label: trimmed, emoji: emoji || "🎼", photo_data_url: photoDataUrl });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const EMOJI_CHOICES = ["🎼","🎻","🎺","🎷","🥁","🪕","🎸","🎹","🪈","🎤","🎵","🪗"];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="custom-instrument-dialog">
        <DialogHeader>
          <DialogTitle>{t("Add a custom instrument")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm -mt-2" style={{ color: "var(--app-text-muted)" }}>
          {t("Studio Pro perk — track any instrument you play, even if it's not on our list.")}
        </p>
        <div className="space-y-4 mt-3">
          <div>
            <Label>{t("Instrument name")}</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("e.g. Erhu, Hurdy-gurdy, Theremin")}
                   maxLength={60} data-testid="custom-inst-label" />
          </div>
          <div>
            <Label>{t("Icon")}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="custom-inst-emoji-row">
              {EMOJI_CHOICES.map((e) => (
                <button key={e} type="button" onClick={() => setEmoji(e)}
                        data-testid={`custom-emoji-${e}`}
                        className={`w-10 h-10 rounded-lg border-2 text-xl ${emoji === e ? "ring-2" : ""}`}
                        style={{ borderColor: emoji === e ? "var(--app-accent)" : "var(--app-border)",
                                 background: emoji === e ? "rgba(255,183,3,0.10)" : "var(--app-surface)" }}>
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("Photo (optional)")}</Label>
            <div className="flex items-center gap-3 mt-1">
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid="custom-inst-photo-input" className="text-xs" />
              {photoDataUrl && <img src={photoDataUrl} alt="" className="w-20 h-14 object-cover rounded-lg" />}
            </div>
          </div>
          <Button onClick={submit} disabled={busy} className="btn-pill btn-primary h-11 w-full justify-center" data-testid="custom-inst-save">
            {busy ? t("Saving...") : t("Add to my roster")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InstrumentDetailDialog({ open, entry, onClose, onRemove, onSaveProfile, onSaveDream }) {
  const t = useT();
  if (!entry) return null;
  const key = entry.instrument;
  const isCustom = key.startsWith("custom_");
  const customMeta = entry.custom_meta || {};
  const [primary, , fg] = instrumentPalette(key);
  const label = customMeta.label || instrumentLabel(key);
  const emoji = customMeta.emoji || instrumentEmoji(key);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 -mx-6 -mt-6 mb-2 px-6 py-4 rounded-t-xl" style={{ background: primary, color: fg }}>
            <span className="text-3xl">{emoji}</span>
            <DialogTitle className="font-display text-2xl tracking-tight uppercase" style={{ color: fg }}>
              {label}{isCustom && <span className="ml-2 text-xs px-2 py-0.5 rounded-full align-middle" style={{ background: "rgba(255,255,255,0.18)" }}>{t("CUSTOM")}</span>}
            </DialogTitle>
            <button type="button" onClick={onClose} className="ml-auto rounded-full p-1 hover:bg-black/20" aria-label={t("Close")}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <div className="bento-card p-3 text-center">
            <div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Trophies")}</div>
            <div className="font-display text-3xl font-bold">{entry.trophies || 0}</div>
          </div>
          <div className="bento-card p-3 text-center">
            <div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Scales")}</div>
            <div className="font-display text-3xl font-bold">{entry.scales_perfected || 0}/12</div>
          </div>
          <div className="bento-card p-3 text-center">
            <div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Songs")}</div>
            <div className="font-display text-3xl font-bold">{entry.songs_total || 0}</div>
          </div>
          <div className="bento-card p-3 text-center">
            <div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Perfected")}</div>
            <div className="font-display text-3xl font-bold">{entry.songs_perfected || 0}</div>
          </div>
        </div>

        <div className="bento-card p-5 mb-4">
          <h3 className="font-display font-bold text-xl mb-3">The {label.toLowerCase()} I play</h3>
          <InstrumentEditor
            instrumentKey={key}
            initial={entry.profile}
            onSave={onSaveProfile}
            testPrefix={`inst-${key}`}
          />
        </div>
        <div className="bento-card p-5 mb-4">
          <h3 className="font-display font-bold text-xl mb-3">My dream {label.toLowerCase()}</h3>
          <InstrumentEditor
            instrumentKey={key}
            initial={entry.dream_profile}
            onSave={onSaveDream}
            dream
            testPrefix={`dream-${key}`}
          />
        </div>
        <div className="flex justify-end">
          <Button variant="destructive" onClick={onRemove} data-testid={`remove-instrument-${key}`}>
            <Trash2 className="w-4 h-4 mr-1" /> {t("Remove this instrument")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyInstrumentPage() {
  const t = useT();
  const { user } = useAuth();
  const { refresh: refreshContext, label: instrumentLabelLocal, emoji: instrumentEmojiLocal, customInstruments } = useInstrument();
  const [roster, setRoster] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [detailKey, setDetailKey] = useState(null);
  const [pricing, setPricing] = useState(null);

  const load = async () => {
    const r = await apiClient.get("/me/instruments-roster");
    setRoster(r.data.roster);
  };
  useEffect(() => { load(); apiClient.get("/subscription/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);

  const keys = useMemo(() => (roster || []).map((e) => e.instrument), [roster]);
  const detailEntry = useMemo(() => (roster || []).find((e) => e.instrument === detailKey), [roster, detailKey]);
  const isPremium = !!user?.premium || user?.role === "developer";
  const isSolo = !!user?.solo_learner;

  // Dynamic upsell labels — solo learners get a cheaper +$0.99 addon ($3.98 total),
  // teachers get the +$6 addon ($15.99 total) which also unlocks unlimited students.
  const addonUsd = pricing?.premium_addon_usd ?? (isSolo ? 0.99 : 6.00);
  const totalUsd = (pricing?.base_price_usd ?? (isSolo ? 2.99 : 9.99)) + addonUsd;
  const upsellLabel = `${t("Studio Pro")} · $${totalUsd.toFixed(2)}/mo`;
  const upsellToast = `${t("Custom instruments are a Studio Pro perk.")} ${t("Upgrade for")} $${addonUsd.toFixed(2)}/mo ${t("to unlock.")}`;

  const openCustomDialog = () => {
    if (!isPremium) {
      toast.info(upsellToast);
      return;
    }
    setAddOpen(false);
    setCustomOpen(true);
  };

  const createCustom = async ({ label, emoji, photo_data_url }) => {
    try {
      const r = await apiClient.post("/me/custom-instrument", { label, emoji, photo_data_url });
      await refreshContext();
      await load();
      const newKey = r.data.key;
      setDetailKey(newKey);
      toast.success(`${label} ${t("added to your roster").replace(/^./, c => c.toLowerCase())}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) || (typeof detail === "string" ? detail : t("Couldn't add instrument"));
      toast.error(msg);
    }
  };

  const handlePickNew = async (key) => {
    try {
      const next = [...keys, key];
      await apiClient.put("/me/instruments", { instruments: next });
      await refreshContext();
      await load();
      setAddOpen(false);
      setDetailKey(key);  // immediately open detail to fill in make/model/etc
      toast.success(`${instrumentLabelLocal(key)} ${t("added to your roster").replace(/^./, c => c.toLowerCase())}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "instrument_limit_reached") {
        toast.error(detail.message);
      } else {
        toast.error(detail || t("Couldn't add instrument"));
      }
    }
  };

  const saveProfile = async (data) => {
    await apiClient.put(`/me/instrument-profile/${detailKey}`, data);
    await load();
  };
  const saveDream = async (data) => {
    await apiClient.put(`/me/dream-instrument-profile/${detailKey}`, data);
    await load();
  };
  const removeInstrument = async () => {
    if (!detailKey) return;
    if (!window.confirm(`${t("Remove")} ${instrumentLabelLocal(detailKey)} ${t("from your roster?")} ${t("Songs and scales tied to it will remain, but the instrument won't appear in your tabs.")}`)) return;
    if (keys.length <= 1) { toast.error(t("You need at least one instrument.")); return; }
    if (detailKey.startsWith("custom_")) {
      await apiClient.delete(`/me/custom-instrument/${detailKey}`);
    } else {
      const next = keys.filter((k) => k !== detailKey);
      await apiClient.put("/me/instruments", { instruments: next });
    }
    await refreshContext();
    setDetailKey(null);
    await load();
    toast.success(t("Removed"));
  };

  if (roster === null) return <div>{t("Loading…")}</div>;

  return (
    <div className="space-y-6" data-testid="my-instruments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("My Instruments")}</h1>
          <p style={{ color: "var(--app-text-muted)" }}>
            {t("Your roster. Tap a fighter to see stats, edit gear, or manage progress.")}
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="btn-pill btn-primary h-11" data-testid="add-instrument-btn">
          <Plus className="w-4 h-4" /> {t("Add instrument")}
        </Button>
      </div>

      {roster.length === 0 ? (
        <div className="bento-card p-10 text-center">
          <div className="text-5xl mb-2">🎼</div>
          <p style={{ color: "var(--app-text-muted)" }}>{t("No instruments yet. Add your first one to start tracking songs, scales, and trophies.")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="instrument-roster">
          {roster.map((entry) => (
            <FighterCard
              key={entry.instrument}
              entry={entry}
              onOpen={() => setDetailKey(entry.instrument)}
            />
          ))}
        </div>
      )}

      <AddInstrumentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        currentKeys={keys}
        onPick={handlePickNew}
        onCreateCustom={openCustomDialog}
        isPremium={isPremium}
        upsellLabel={upsellLabel}
      />

      <CustomInstrumentDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        onCreate={createCustom}
      />

      <InstrumentDetailDialog
        open={!!detailKey}
        entry={detailEntry}
        onClose={() => setDetailKey(null)}
        onRemove={removeInstrument}
        onSaveProfile={saveProfile}
        onSaveDream={saveDream}
      />
    </div>
  );
}
